import React, { useState } from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Clock, Tag, Search, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { useToast } from "../hooks/use-toast";
import axios from 'axios';

const blogPosts = [
  {
    id: 1,
    title: "Understanding Your Child's Emotional Development",
    excerpt: "Learn about the key stages of emotional growth and how to support your child through each phase.",
    category: "Parenting Tips",
    readTime: "5 min",
    date: "Dec 15, 2024",
    image: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=800&q=80"
  },
  {
    id: 2,
    title: "Managing Anxiety in Adolescents",
    excerpt: "Practical strategies to help teenagers cope with stress and anxiety in today's challenging world.",
    category: "Adolescent Care",
    readTime: "7 min",
    date: "Dec 10, 2024",
    image: "https://images.unsplash.com/photo-1509099836639-18ba1795216d?w=800&q=80"
  },
  {
    id: 3,
    title: "Building Strong Family Connections",
    excerpt: "Simple daily practices that strengthen family bonds and improve communication.",
    category: "Family Therapy",
    readTime: "4 min",
    date: "Dec 5, 2024",
    image: "https://images.unsplash.com/photo-1511895426328-dc8714191300?w=800&q=80"
  },
  {
    id: 4,
    title: "Recognizing Signs of Depression in Children",
    excerpt: "Important behavioral changes parents should watch for and when to seek professional help.",
    category: "Mental Health",
    readTime: "6 min",
    date: "Dec 1, 2024",
    image: "https://images.unsplash.com/photo-1517164850305-99a3e65bb47e?w=800&q=80"
  },
  {
    id: 5,
    title: "Effective Discipline Techniques",
    excerpt: "Evidence-based approaches to discipline that teach without damaging the parent-child relationship.",
    category: "Parenting Tips",
    readTime: "8 min",
    date: "Nov 28, 2024",
    image: "https://images.unsplash.com/photo-1491146179969-d674118945ff?w=800&q=80"
  },
  {
    id: 6,
    title: "Supporting Children Through Transitions",
    excerpt: "How to help your child adapt to major life changes like moving, divorce, or starting a new school.",
    category: "Parenting Tips",
    readTime: "5 min",
    date: "Nov 20, 2024",
    image: "https://images.unsplash.com/photo-1472162072942-cd5147eb3902?w=800&q=80"
  }
];

export const ResourcesPage = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  const categories = ['All', 'Parenting Tips', 'Mental Health', 'Adolescent Care', 'Family Therapy'];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubscribing(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${API_URL}/api/contact/newsletter`, {
        email: email
      });

      if (response.data.success) {
        toast({
          title: "Successfully Subscribed!",
          description: response.data.message || "You'll receive parenting tips and mental health resources.",
        });
        setEmail('');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to subscribe. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 via-blue-50 to-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-4 bg-purple-100 text-purple-800">Parenting Resources</Badge>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Expert Guidance for Parents
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Evidence-based articles, practical tips, and expert insights to help you 
            navigate the challenges of parenting with confidence.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-6 text-lg border-2 border-gray-300 focus:border-teal-500 transition-colors"
            />
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-50 border-y">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => setSelectedCategory(category)}
                className={
                  selectedCategory === category
                    ? "bg-teal-600 hover:bg-teal-700 text-white transition-all duration-200"
                    : "border-2 border-gray-300 text-gray-700 hover:border-teal-600 hover:text-teal-600 transition-all duration-200"
                }
              >
                <Tag className="w-4 h-4 mr-2" />
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-gray-600">No articles found. Try a different search or category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Card 
                  key={post.id} 
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer bg-white overflow-hidden group hover:-translate-y-1"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <Badge className="absolute top-4 left-4 bg-white text-gray-800 shadow-md">
                      {post.category}
                    </Badge>
                  </div>
                  <CardHeader>
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <span>{post.date}</span>
                      <span className="mx-2">•</span>
                      <Clock className="w-4 h-4 mr-1" />
                      <span>{post.readTime}</span>
                    </div>
                    <CardTitle className="text-xl group-hover:text-teal-600 transition-colors leading-tight">
                      {post.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 text-base mb-4">
                      {post.excerpt}
                    </CardDescription>
                    <Button 
                      variant="link" 
                      className="text-teal-600 hover:text-teal-700 p-0 h-auto font-semibold"
                    >
                      Read More →
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-teal-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Stay Updated with Parenting Tips
          </h2>
          <p className="text-lg mb-8 opacity-95">
            Get the latest articles and expert advice delivered to your inbox
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 w-full">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isSubscribing}
                className="flex-1 py-6 px-4 text-gray-900 border-none disabled:opacity-50"
              />
              <Button 
                type="submit"
                size="lg"
                disabled={isSubscribing}
                className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-6 font-semibold transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubscribing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  'Subscribe'
                )}
              </Button>
            </form>
          </div>
          <p className="text-sm mt-4 opacity-80">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </div>
      </section>
    </div>
  );
};
